import { useState } from "react";
import { useRecordContext, useUpdate, useResourceContext, useNotify, useRefresh, type Validator } from "react-admin";
import { Box, Typography, IconButton, TextField, Tooltip, Chip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { checkBrandContrast, isValidHexColor, type ContrastCheckResult, type WcagLevel } from "@/utils/colorUtils";
import { ValidAny } from "@/types";
import { pickBranding, syncBrandingCache } from "@/utils/brandingSync";
import { putPartnerBranding } from "@/utils/partnerBranding";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface InPlaceColorEditorProps {
  source: string;
  label: string;
  resource?: string;
  helperText?: string;
  showButtons?: boolean;
  readOnly?: boolean;
  readOnlyReason?: string;
  // Accepted for EditorComponentProps compatibility; color validation is handled internally
  validate?: Validator | Validator[];
}

const levelColors: Record<WcagLevel, string> = {
  pass: "#2E7D32",
  "large-only": "#ED6C02",
  fail: "#D32F2F",
};

const levelLabels: Record<WcagLevel, string> = {
  pass: "AA Pass",
  "large-only": "Large text only",
  fail: "Fails AA",
};

/**
 * Compact inline contrast result row.
 */
const ContrastRow = ({ result }: { result: ContrastCheckResult }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
    {result.level === "fail" ? (
      <WarningAmberIcon sx={{ fontSize: 14, color: levelColors.fail }} />
    ) : result.level === "large-only" ? (
      <WarningAmberIcon sx={{ fontSize: 14, color: levelColors["large-only"] }} />
    ) : (
      <CheckCircleOutlineIcon sx={{ fontSize: 14, color: levelColors.pass }} />
    )}
    <Typography variant="caption" sx={{ flex: 1, fontSize: "11px" }}>
      {result.context}
    </Typography>
    <Chip
      label={`${result.ratio}:1 · ${levelLabels[result.level]}`}
      size="small"
      sx={{
        height: 18,
        fontSize: "10px",
        fontWeight: 600,
        bgcolor: `${levelColors[result.level]}14`,
        color: levelColors[result.level],
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  </Box>
);

const InPlaceColorEditor = ({
  source,
  label,
  resource: resourceProp,
  showButtons = false,
  readOnly = false,
  readOnlyReason,
}: InPlaceColorEditorProps) => {
  const record = useRecordContext();
  const contextResource = useResourceContext();
  const resource = resourceProp || contextResource;
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  // Get nested value from source path
  const getValue = (path: string) => path.split(".").reduce((acc: ValidAny, k) => acc?.[k], record);

  const currentValue = getValue(source) || "";
  const [editing, setEditing] = useState(false);
  const [color, setColor] = useState(currentValue);
  const [update, { isLoading }] = useUpdate();
  const [saving, setSaving] = useState(false);
  const refresh = useRefresh();

  // Determine if this is a dark mode color field
  const isDarkField = source.includes("dark");
  const contrastMode = isDarkField ? "dark" : "light";

  // Get contrast results for the current color being edited
  const contrastResults = isValidHexColor(color) ? checkBrandContrast(color, contrastMode) : [];
  const hasFailures = contrastResults.some((r) => r.level === "fail");
  const hasWarnings = contrastResults.some((r) => r.level === "large-only");

  const handleSave = () => {
    const parts = source.split(".");

    // Partner branding writes to the dedicated branding column via its own endpoint
    // (#4116 / #5360), NOT the generic partner resource update / configuration.branding.
    if (resource === "partners" && parts[0] === "branding") {
      const existing = (pickBranding(record) ?? {}) as Record<string, ValidAny>;
      const merged = { ...existing, [parts[parts.length - 1]]: color };
      setSaving(true);
      void putPartnerBranding(String(record?.id), merged)
        .then(() => {
          setEditing(false);
          notify("Color updated", { type: "success" });
          syncBrandingCache({ ...record, branding: merged });
          refresh();
        })
        .catch((error) => notifyError(error))
        .finally(() => setSaving(false));
      return;
    }

    let payload: Record<string, ValidAny>;

    if (parts.length > 1) {
      // Deep merge: rebuild from the record's existing data so sibling fields aren't wiped
      // e.g., for "configuration.branding.brand_primary", we clone the full
      // configuration object and only update the one leaf field
      const rootKey = parts[0];
      const existingRoot = record?.[rootKey] ?? {};

      const merged = JSON.parse(JSON.stringify(existingRoot));

      // Walk to the parent of the leaf and set the value
      let current = merged;
      for (let i = 1; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = color;

      payload = { [rootKey]: merged };
    } else {
      payload = { [parts[0]]: color };
    }

    void update(
      resource,
      { id: record?.id, data: payload, previousData: record },
      {
        onSuccess: () => {
          setEditing(false);
          notify("Color updated", { type: "success" });
        },
        onError: (error) => notifyError(error),
      }
    );
  };

  const handleCancel = () => {
    setColor(currentValue);
    setEditing(false);
  };

  if (!editing) {
    return (
      <Box
        sx={{ cursor: readOnly ? "default" : "pointer" }}
        onClick={() => !readOnly && setEditing(true)}
        title={readOnly ? readOnlyReason : undefined}
      >
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          {currentValue ? (
            <>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  backgroundColor: currentValue,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {currentValue?.toUpperCase()}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>

      {/* Contrast validation results — above picker so they're visible while picking */}
      {isValidHexColor(color) && contrastResults.length > 0 && (
        <Box
          sx={{
            mt: 0.5,
            mb: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: hasFailures
              ? "rgba(211, 47, 47, 0.06)"
              : hasWarnings
                ? "rgba(237, 108, 2, 0.06)"
                : "rgba(46, 125, 50, 0.06)",
            border: "1px solid",
            borderColor: hasFailures
              ? "rgba(211, 47, 47, 0.2)"
              : hasWarnings
                ? "rgba(237, 108, 2, 0.2)"
                : "rgba(46, 125, 50, 0.2)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              fontSize: "11px",
              color: hasFailures ? levelColors.fail : hasWarnings ? levelColors["large-only"] : levelColors.pass,
              mb: 0.5,
              display: "block",
            }}
          >
            {hasFailures
              ? "⚠ Contrast issues detected"
              : hasWarnings
                ? "⚠ Limited contrast for small text"
                : "✓ Meets WCAG AA contrast requirements"}
          </Typography>
          {contrastResults.map((result, i) => (
            <ContrastRow key={i} result={result} />
          ))}
        </Box>
      )}

      {/* Color picker row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
        <input
          type="color"
          value={isValidHexColor(color) ? color : "#000000"}
          onChange={(e) => setColor(e.target.value)}
          style={{
            width: 36,
            height: 36,
            padding: 0,
            border: "none",
            cursor: "pointer",
            borderRadius: 4,
          }}
        />
        <TextField
          size="small"
          value={color}
          onChange={(e) => {
            const val = e.target.value;
            if (/^#?[0-9A-Fa-f]{0,6}$/.test(val)) {
              setColor(val.startsWith("#") ? val : `#${val}`);
            }
          }}
          error={color.length > 1 && !isValidHexColor(color)}
          sx={{ width: 120 }}
          slotProps={{
            input: {
              sx: { fontFamily: "monospace", fontSize: "0.875rem" },
            },
          }}
        />
        {showButtons && (
          <>
            <Tooltip title={hasFailures ? "Color has contrast issues — save anyway?" : "Save"}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleSave}
                  disabled={isLoading || saving || !isValidHexColor(color)}
                  color={hasFailures ? "warning" : "primary"}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={handleCancel} disabled={isLoading || saving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default InPlaceColorEditor;
